import XLSX from 'xlsx-js-style'

const remoteDataCache = {}

export default {
	async loadRemoteData(store, template, payload = null, force = false) {
		const cacheKey = template.id && template.id + (payload ? '|' + JSON.stringify(payload) : '')

		if (!force && cacheKey && remoteDataCache[cacheKey]) {
			return { ...remoteDataCache[cacheKey] }
		}

		const data = {}

		// Load remote data from template specs
		const remoteDataKeys = Object.keys(template.remoteData || {})

		for (let i = 0; i < remoteDataKeys.length; i++) {
			const spec = template.remoteData[remoteDataKeys[i]]
			let result = null

			// Load data with a store action
			if (spec.action) {
				const actionName = (spec.module ? spec.module + '/' + spec.action : spec.action)
				
				let remoteDataPayload = ({...spec.payload} || {})

				if (payload) {
					if (!remoteDataPayload) {
						remoteDataPayload = payload
					} else {
						for (const key in payload) {
							remoteDataPayload[key] = payload[key]
						}
					}
				}

				if (spec.remoteDataPayload) {
					spec.remoteDataPayload.forEach((key) => {
						remoteDataPayload[key] = (data[key] || undefined)
					})
				}

				result = await store.dispatch(actionName, (remoteDataPayload || null))

				// Get result from a store state if needed
				if (spec.state) {
					if (spec.module) {
						result = store.state[spec.module][spec.state]
					} else {
						result = store.state[spec.state]
					}
				}
			} else if (spec.data) {
				result = spec.data
			}

			// Store data result
			data[remoteDataKeys[i]] = result

			// Create default group by if needed
			if (spec.groupBy) {
				let fields = spec.groupBy
				
				if (!Array.isArray(fields)) {
					fields = [fields]
				}

				// todo: opti list browsing when multiple groupby
				fields.forEach((field) => {
					this.getGroupByData(data, {
						list: remoteDataKeys[i],
						field,
					})
				})
			}

			if (spec.groupByUniq) {
				let fields = spec.groupByUniq
				
				if (!Array.isArray(fields)) {
					fields = [fields]
				}

				// todo: opti list browsing when multiple groupby
				fields.forEach((field) => {
					this.getGroupByData(data, {
						list: remoteDataKeys[i],
						field,
					}, true)
				})
			}
		}

		if (cacheKey) {
			remoteDataCache[cacheKey] = data
		}

		return { ...data }
	},
	async loadData(store, template, payload = null, force = false) {
		// Load remote data from template specs
		const data = await this.loadRemoteData(store, template, payload, force)

		// Compute other data
		const extraDataKeys = Object.keys(template.extraData || {})

		extraDataKeys.forEach((key) => {
			if (!data[key]) {
				if (typeof template.extraData[key] == "function") {
					// Store new data
					data[key] = template.extraData[key](data, payload)
				}
			} else {
				// Add extra fields to an existing list
				const extraFieldsKeys = Object.keys(template.extraData[key])

				if (data[key].forEach) {
					data[key].forEach((item) => {
						extraFieldsKeys.forEach((fieldKey) => {
							item[fieldKey] = template.extraData[key][fieldKey](item, data, payload)
						})
					})
				} else {
					extraFieldsKeys.forEach((fieldKey) => {
						data[key][fieldKey] = template.extraData[key][fieldKey](data[key], data, payload)
					})
				}
			}
		})

		return data
	},
	getGroupByData(data, { list, field }, uniq = false) {
		// Create a camel case suffix
		const suffix = field.split('_').map((word) => (word.charAt(0).toUpperCase() + word.slice(1))).join('')
		
		// Group data by field value if needed
		const groupByName = list + 'By' + suffix

		if (!data[groupByName]) {
			let reduce = null

			if (!uniq) {
				reduce = (dict, item) => {
					if (!dict[item[field]])
						dict[item[field]] = []

					dict[item[field]].push(item)

					return dict
				}
			} else {
				reduce = (dict, item) => {
					dict[item[field]] = item

					return dict
				}
			}

			data[groupByName] = data[list].reduce(reduce, {})
		}

		return data[groupByName]
	},
	getSheetContent(data, sheetTemplate, sheetCellsByType, sheetCellStyles) {
		// Create sheet content array (only two column for now)
		let content = []

		// Add content from template specs
		for (let i = 0; i < sheetTemplate.content.length; i++) {
			const spec = sheetTemplate.content[i]

			if (spec.cells) {
				let cells = []

				spec.cells.forEach((cell, index) => {
					cells.push(cell.text || (cell.value ? cell.value(data) : ''))

					// Handle cell type
					if (cell.type) {
						const cellRef = XLSX.utils.encode_cell({ c: index, r: content.length })

						sheetCellsByType[cell.type].push(cellRef)
					}

					// Handle cell style
					if (cell.style) {
						const cellRef = XLSX.utils.encode_cell({ c: index, r: content.length })

						sheetCellStyles[cellRef] = cell.style
					}
				})

				content.push(cells)
			} else if (spec.table) {
				// Get list
				let list = (typeof spec.table == 'function' ? spec.table(data) : data[spec.table])
				
				if (!list)
					continue

				// Add table headers
				if (typeof spec.headers == 'function') {
					content.push(spec.headers(list, data))
				} else if (spec.headers && spec.headers.length > 0) {
					content.push(spec.headers)
				}

				// Add each row data
				list.forEach((item) => {
					// Parse row cells
					const row = spec.row(item, data).map((field, index) => {
						if (typeof field === 'string')
							return item[field]

						// Handle field type
						if (field.type) {
							const cellRef = XLSX.utils.encode_cell({ c: index, r: content.length })

							sheetCellsByType[field.type].push(cellRef)
						}

						// Handle field style
						if (field.style) {
							const cellRef = XLSX.utils.encode_cell({ c: index, r: content.length })

							sheetCellStyles[cellRef] = field.style
						}

						return field.value
					})

					content.push(row)
				})
			} else if (spec.groupBy) {
				// Get data list grouped by field value
				const groupByData = this.getGroupByData(data, spec.groupBy)

				// Create a line for each group
				const groupNames = (spec.groupNames || Object.keys(groupByData))

				groupNames.forEach((name) => {
					const text = spec.text(name, groupByData[name], data)
					const value = spec.value(groupByData[name] || [], data)

					// Handle cell type
					if (spec.type) {
						const cellRef = XLSX.utils.encode_cell({ c: 1, r: content.length })

						sheetCellsByType[spec.type].push(cellRef)
					}

					// Handle cell style
					if (spec.style) {
						const cellRef = XLSX.utils.encode_cell({ c: 1, r: content.length })

						sheetCellStyles[cellRef] = spec.style
					}

					content.push([text, value])
				})
			} else {
				let line = []

				if (spec.text != undefined) {
					line.push(spec.text || '')
				}

				if (spec.value) {
					line.push(spec.value(data))
				} else {
					line.push('')
				}

				// Handle cell type
				if (spec.type) {
					const cellRef = XLSX.utils.encode_cell({ c: 1, r: content.length })

					sheetCellsByType[spec.type].push(cellRef)
				}

				// Handle cell value style
				if (spec.style) {
					const cellRef = XLSX.utils.encode_cell({ c: 1, r: content.length })

					sheetCellStyles[cellRef] = spec.style
				}

				// Handle cell text style
				if (spec.textStyle) {
					const cellRef = XLSX.utils.encode_cell({ c: 0, r: content.length })

					sheetCellStyles[cellRef] = spec.textStyle
				}

				content.push(line)
			}
		}

		return content
	},
	getSheet(sheetTemplate, data, template) {
		// Init sheet cells type dictionary
		let sheetCellsByType = Object.keys(template.types).reduce((dict, type) => {
			dict[type] = []

			return dict
		}, {})

		let sheetCellStyles = {}

		// Get content
		const content = this.getSheetContent(data, sheetTemplate, sheetCellsByType, sheetCellStyles)

		// Create sheet from content array
		const sheet = XLSX.utils.aoa_to_sheet(content)

		// console.log(data)
		// console.table(content)

		// Add custom formats to specified sheet cells
		const sheetTypes = Object.keys(sheetCellsByType)

		sheetTypes.forEach((typeName) => {
			const type = template.types[typeName]

			if (typeof type === 'string') {
				sheetCellsByType[typeName].forEach((cellRef) => {
					if (!sheet[cellRef]) {
						return
					}

					sheet[cellRef].z = type
				})
			} else if (type.transform) {
				sheetCellsByType[typeName].forEach((cellRef) => {
					if (!sheet[cellRef]) {
						return
					}
					
					const value = type.transform(sheet[cellRef].v)

					if (value !== false) {
						sheet[cellRef].v = value
						sheet[cellRef].z = type.format
					} else {
						sheet[cellRef].v = '-'
						sheet[cellRef].t = 's'
					}
				})
			}
		})

		// Add custom styles to specified sheet cells
		Object.keys(sheetCellStyles).forEach((cellRef) => {
			if (!sheet[cellRef]) {
				return
			}

			sheet[cellRef].s = sheetCellStyles[cellRef]
		})

		return sheet
	},
	addData(file, data, template) {
		if (!template.sheets)
			return

		// Add data for each sheet
		template.sheets.forEach((sheetTemplate) => {
			if (!sheetTemplate.name)
				return

			// Handle dynamic sheets from a list
			if (sheetTemplate.list) {
				const list = data[sheetTemplate.list]

				if (!list)
					return

				for (var i = 0; i < list.length; i++) {
					data[sheetTemplate.itemName] = list[i]

					const sheet = this.getSheet(sheetTemplate, data, template)

					// Add sheet to file
					XLSX.utils.book_append_sheet(file, sheet, sheetTemplate.name(list[i]))
				}
			} else {
				// Handle simple sheet
				const sheet = this.getSheet(sheetTemplate, data, template)

				// Add sheet to file
				XLSX.utils.book_append_sheet(file, sheet, sheetTemplate.name)
			}
		})
	},
	async export(fileNamePrefix, store, template, payload = null, loadedData = null) {
		// Create new file
		const file = XLSX.utils.book_new()

		// Load tracking data for template
		const data = (loadedData || await this.loadData(store, template, payload, true))

		// Add formated data to file
		this.addData(file, data, template)

		// Create file name formated like: YearMonthDay_Rapport.xlsx
		const today = new Date()
		const fileName = `${fileNamePrefix.replaceAll(' ', '_').substring(0, 60)}_${today.getDate()}-${today.getMonth() + 1}-${today.getFullYear()}.xlsx`
		
		// Write file to download output
		XLSX.writeFile(file, fileName)
	},
}